// Red de seguridad de las herramientas internas.
//
// El 28/08 ANDI se quedó en blanco para el equipo y no había forma de saber por
// qué: un error de JavaScript deja la página vacía, sin rastro en ninguna parte.
// Esto hace dos cosas: enseñar al usuario un mensaje con sentido en vez de una
// pantalla blanca, y dejar el error escrito en `clientErrors` para poder mirarlo
// después.
import { Component, ErrorInfo, ReactNode } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

export const logClientError = (app: string, error: any, extra: Record<string, any> = {}) => {
  try {
    addDoc(collection(db, "clientErrors"), {
      app,
      message: String(error?.message || error).slice(0, 500),
      stack: String(error?.stack || "").slice(0, 4000),
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
      by: auth.currentUser?.email || "sin sesión",
      at: serverTimestamp(),
      ...extra,
    }).catch(() => {});
  } catch {
    /* si ni siquiera se puede registrar, no montamos otro error encima */
  }
};

// Errores sueltos fuera de React (promesas sin capturar, etc.).
export const captureGlobalErrors = (app: string) => {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => logClientError(app, e.error || e.message, { tipo: "window.error" }));
  window.addEventListener("unhandledrejection", (e: any) =>
    logClientError(app, e.reason, { tipo: "promesa sin capturar" })
  );
};

interface Props { app: string; children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logClientError(this.props.app, error, { componentStack: String(info.componentStack || "").slice(0, 2000) });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Poppins, system-ui, sans-serif", background: "#f7f9fc" }}>
        <div style={{ maxWidth: 480, background: "#fff", borderRadius: 16, padding: 28, boxShadow: "0 10px 30px rgba(0,0,0,.08)", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px", color: "#1b2559" }}>Algo ha fallado al cargar</h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "#5a6484", margin: "0 0 18px" }}>
            La herramienta no ha podido abrirse. Ya me ha llegado el aviso con el detalle del error, así que
            estoy en ello. Prueba a recargar; si sigue igual, escríbelo en el tablero de peticiones.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ background: "#2b76e5", color: "#fff", border: 0, borderRadius: 10, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Recargar
          </button>
          <p style={{ fontSize: 11, color: "#98a2b3", marginTop: 16, wordBreak: "break-word" }}>
            {String(this.state.error?.message || "").slice(0, 200)}
          </p>
        </div>
      </div>
    );
  }
}
